import { supabase } from "@/lib/supabase-client";
import { ProjectType } from "@/types/db/projects";
import { FileMetadata } from "@/types/files";
import { shiftsService } from "./shiftService";

export const projectService = {
  async getProjects(archived = false): Promise<ProjectType[]> {
    const { data, error } = await supabase
      .from("projects")
      .select(
        `id, status, name, meta_data, archived, shifts(start_time, end_time), client: clients(id, name, meta_data), location_id, requested_by, onsite_contact, primary_contact, created_at`,
      )
      .eq("archived", archived)
      .order("created_at", { ascending: false });

    if (error) {
      throw Error(error.message);
    }

    // Extract unique location_ids and contact ids
    const locationIds = new Set<string>();
    const contactIds = new Set<string>();
    data?.forEach((project) => {
      if (project.location_id) {
        locationIds.add(project.location_id);
      }
      if (project.requested_by) {
        contactIds.add(project.requested_by);
      }
      if (project.onsite_contact) {
        contactIds.add(project.onsite_contact);
      }
      if (project.primary_contact) {
        contactIds.add(project.primary_contact);
      }
    });

    // Query lists.locations for location data
    let locationsMap = new Map<string, any>();
    if (locationIds.size > 0) {
      const { data: locations } = await supabase
        .schema("lists")
        .from("locations")
        .select("id, name, street, city, state, zip, instructions, market_id")
        .in("id", Array.from(locationIds));

      if (locations) {
        locations.forEach((loc) => {
          locationsMap.set(loc.id, loc);
        });
      }
    }

    // Query contacts for requested_by data
    let contactsMap = new Map<string, any>();
    if (contactIds.size > 0) {
      const { data: contacts } = await supabase
        .from("contacts")
        .select("id, first_name, last_name, preferred_name")
        .in("id", Array.from(contactIds));

      if (contacts) {
        contacts.forEach((contact) => {
          contactsMap.set(contact.id, contact);
        });
      }
    }

    // Enrich with linked_record shapes for location and contact data
    return data.map((project) => {
      const location = project.location_id ? locationsMap.get(project.location_id) : null;
      const requested_by_raw = project.requested_by ? contactsMap.get(project.requested_by) : null;
      const onsite_contact_raw = project.onsite_contact ? contactsMap.get(project.onsite_contact) : null;
      const primary_contact_raw = project.primary_contact ? contactsMap.get(project.primary_contact) : null;

      const toLinkedRecord = (contact: any) =>
        contact
          ? { name: `${contact.preferred_name || contact.first_name} ${contact.last_name}`, id: contact.id }
          : null;

      // Supabase types the to-one `client` embed as an array; it's a single object at runtime.
      const clientEmbed = project.client as unknown as { id: string; name: string; meta_data: unknown } | null;
      return {
        ...project,
        client: clientEmbed ? { name: clientEmbed.name, id: clientEmbed.id, meta_data: clientEmbed.meta_data } : null,
        location: location ? { name: location.name, id: location.id, state: location.state } : null,
        requested_by_contact: toLinkedRecord(requested_by_raw),
        onsite_contact: toLinkedRecord(onsite_contact_raw),
        primary_contact: toLinkedRecord(primary_contact_raw),
        start_time: project.shifts?.length
          ? new Date(Math.min(...project.shifts.map((s) => new Date(s.start_time).getTime()))).toISOString()
          : null,
        end_time: project.shifts?.length
          ? new Date(Math.max(...project.shifts.map((s) => new Date(s.end_time).getTime()))).toISOString()
          : null,
        // Merge locationMarket into markets array (deduplicated, location first)
        markets: (() => {
          const metaMarkets = (project.meta_data as any)?.markets || [];
          const locationMarketId = location?.market_id || null;
          return locationMarketId && !metaMarkets.includes(locationMarketId)
            ? [locationMarketId, ...metaMarkets]
            : metaMarkets;
        })(),
        // Location's market ID for readonly styling in table
        locationMarket: location?.market_id || null,
        // Extract billing_status from meta_data for table display
        billing_status: (project.meta_data as any)?.billing_status || null,
        deposit_amount: (project.meta_data as any)?.deposit_amount ?? null,
      };
    }) as unknown as ProjectType[];
  },

  async getProjectById(id: string): Promise<ProjectType | null> {
    const { data, error } = await supabase.rpc("get_project", { p_project_id: id });
    if (error) throw Error(error.message);
    return data as ProjectType;
  },

  async getProjectsByClientId(client_id: string): Promise<ProjectType[]> {
    const { data, error } = await supabase
      .from("projects")
      .select("*, shifts(start_time, end_time)")
      .eq("client_id", client_id)
      .order("created_at", { ascending: false });

    if (error) {
      throw Error(error.message);
    }

    // Calculate start_time and end_time from shifts (earliest start, latest end)
    return data.map((project) => ({
      ...project,
      start_time: project.shifts?.length
        ? new Date(Math.min(...project.shifts.map((s: any) => new Date(s.start_time).getTime()))).toISOString()
        : null,
      end_time: project.shifts?.length
        ? new Date(Math.max(...project.shifts.map((s: any) => new Date(s.end_time).getTime()))).toISOString()
        : null,
    })) as ProjectType[];
  },

  async addProject({ client_id, tenant_id, name }: ProjectType): Promise<ProjectType | null> {
    const { data, error } = await supabase.from("projects").insert([{ client_id, tenant_id, name }]).select().single();
    if (error) {
      throw Error(error.message);
    }
    return data as ProjectType;
  },

  async createProjectWithShifts(projectData: {
    name: string;
    status: string;
    client_id: string;
    meta_data?: {
      [key: string]: any;
    };
    // Contact and location fields
    requested_by?: string;
    primary_contact?: string;
    onsite_contact?: string;
    location_id?: string;
    shifts: {
      name?: string;
      description?: string;
      start_time?: string;
      end_time?: string;
      meta_data?: any;
      positions: {
        position_id: string; // FK to lists.positions (required)
        name?: string;
        description?: string;
        quantity?: number;
        supervisory?: boolean;
        location?: { id?: string; name?: string; [key: string]: any }; // Custom location for position
      }[];
    }[];
  }): Promise<{ project: ProjectType; shifts: any[] }> {
    // Get tenant_id from the client
    const { data: client, error: clientError } = await supabase
      .from("clients")
      .select("tenant_id")
      .eq("id", projectData.client_id)
      .single();

    if (clientError || !client) {
      throw new Error(`Failed to fetch client: ${clientError?.message || "Client not found"}`);
    }

    // Start transaction by creating project first
    const { data: project, error: projectError } = await supabase
      .from("projects")
      .insert([
        {
          name: projectData.name,
          status: projectData.status,
          client_id: projectData.client_id,
          tenant_id: client.tenant_id,
          meta_data: projectData.meta_data || {},
          // Contact and location fields
          requested_by: projectData.requested_by || null,
          primary_contact: projectData.primary_contact || null,
          onsite_contact: projectData.onsite_contact || null,
          location_id: projectData.location_id || null,
        },
      ])
      .select()
      .single();

    if (projectError) {
      throw new Error(`Failed to create project: ${projectError.message}`);
    }

    // Create shifts for the project
    const createdShifts: any[] = [];
    for (const shiftData of projectData.shifts) {
      // Create shift (without positions in meta_data)
      const { data: shift, error: shiftError } = await supabase
        .from("shifts")
        .insert([
          {
            project_id: project.id,
            client_id: projectData.client_id,
            tenant_id: client.tenant_id,
            name: shiftData.name || `Shift for ${project.name}`,
            description: shiftData.description,
            start_time: shiftData.start_time || null,
            end_time: shiftData.end_time || null,
            location_id: shiftData.meta_data?.location?.id || null,
            meta_data: shiftData.meta_data || {}, // Don't store positions in meta_data
          },
        ])
        .select()
        .single();

      if (shiftError) {
        console.error(`Failed to create shift: ${shiftError.message}`);
        throw new Error(`Failed to create shift: ${shiftError.message}`);
      }

      // Create shift_positions records for this shift
      for (const pos of shiftData.positions) {
        try {
          await shiftsService.createShiftPosition(
            shift.id,
            pos.position_id, // FK to lists.positions
            pos.location?.id || undefined, // Custom location ID if provided
            pos.quantity || 1,
          );
        } catch (posError: any) {
          console.error(`Failed to create shift position: ${posError.message}`);
          throw new Error(`Failed to create position for shift: ${posError.message}`);
        }
      }

      createdShifts.push(shift);
    }

    return {
      project: project as ProjectType,
      shifts: createdShifts,
    };
  },

  async deleteProject(id: string): Promise<boolean> {
    const { error } = await supabase.from("projects").delete().eq("id", id);
    if (error) {
      throw Error(error.message);
    }
    return true;
  },

  async deleteProjectsByIds(ids: string[]): Promise<boolean> {
    const { error } = await supabase.from("projects").delete().in("id", ids);
    if (error) {
      throw Error(error.message);
    }
    return true;
  },

  async archiveProjects(ids: string[], archived: boolean): Promise<void> {
    const { error } = await supabase.from("projects").update({ archived }).in("id", ids);
    if (error) throw new Error(error.message);
  },

  async updateProject(id: string, patch: Partial<ProjectType>): Promise<ProjectType> {
    const { data, error } = await supabase.from("projects").update(patch).eq("id", id).select().single();
    if (error) throw new Error(error.message);
    return data as ProjectType;
  },

  async saveRowPatches(
    patches: (Partial<ProjectType> & { id: string; markets?: string[]; billing_status?: string })[],
  ): Promise<void> {
    await Promise.all(
      patches.map(async (patch) => {
        // Deep merge meta_data if it contains nested updates
        let finalPatch: any = { ...patch };

        // Remove virtual fields that were added for display
        delete finalPatch.markets;
        delete finalPatch.billing_status;

        if (patch.meta_data) {
          // Fetch current meta_data to merge
          const { data: current } = await supabase.from("projects").select("meta_data").eq("id", patch.id).single();

          // Deep merge the meta_data
          finalPatch.meta_data = {
            ...(current?.meta_data || {}),
            ...patch.meta_data,
          };
        }

        const { error } = await supabase.from("projects").update(finalPatch).eq("id", patch.id);
        if (error) throw new Error(error.message);
      }),
    );
  },

  // File management methods
  async uploadFile(projectId: string, file: File, uploadedBy: string): Promise<FileMetadata> {
    // Get tenant_id from project record
    const { data: tenantData, error: tenantError } = await supabase
      .from("projects")
      .select("tenant_id")
      .eq("id", projectId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get project tenant: ${tenantError?.message || "Project not found"}`);
    }

    const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const filePath = `${tenantData.tenant_id}/${projectId}/${fileId}-${file.name}`;

    // Upload file to storage
    const { error: uploadError } = await supabase.storage.from("projects").upload(filePath, file);

    if (uploadError) {
      throw new Error(`Upload failed: ${uploadError.message}`);
    }

    // Create file metadata (store path, not URL)
    const fileMetadata: FileMetadata = {
      id: fileId,
      name: file.name,
      size: file.size,
      type: file.type,
      url: filePath, // Store path for signed URL generation
      bucket: "projects", // Store bucket name
      uploadedAt: new Date().toISOString(),
      uploadedBy: uploadedBy,
    };

    // No database metadata tracking needed - files are listed directly from storage
    return fileMetadata;
  },

  async getFiles(projectId: string): Promise<FileMetadata[]> {
    // Get tenant_id from project record
    const { data: tenantData, error: tenantError } = await supabase
      .from("projects")
      .select("tenant_id")
      .eq("id", projectId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get project tenant: ${tenantError?.message || "Project not found"}`);
    }

    // List files from storage bucket
    const storagePath = `${tenantData.tenant_id}/${projectId}`;
    const { data: files, error: listError } = await supabase.storage.from("projects").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list project files: ${listError.message}`);
    }

    if (!files || files.length === 0) {
      return [];
    }

    // Convert storage files to FileMetadata
    const fileMetadata: FileMetadata[] = files.map((file) => {
      // Parse fileId from filename (format: {fileId}-{filename})
      const dashIndex = file.name.indexOf("-");
      const fileId = dashIndex > 0 ? file.name.substring(0, dashIndex) : file.name;
      const fileName = dashIndex > 0 ? file.name.substring(dashIndex + 1) : file.name;

      return {
        id: fileId,
        name: fileName,
        size: file.metadata?.size || 0,
        type: file.metadata?.mimetype || "application/octet-stream",
        url: `${storagePath}/${file.name}`, // Store path for signed URL generation
        bucket: "projects", // Store bucket name
        uploadedAt: file.created_at || new Date().toISOString(),
        uploadedBy: "unknown", // Storage doesn't track uploader, would need database for this
      };
    });

    return fileMetadata;
  },

  async deleteFile(projectId: string, fileId: string): Promise<boolean> {
    // Get tenant_id from project record
    const { data: tenantData, error: tenantError } = await supabase
      .from("projects")
      .select("tenant_id")
      .eq("id", projectId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get project tenant: ${tenantError?.message || "Project not found"}`);
    }

    // List files from storage to find the one to delete
    const storagePath = `${tenantData.tenant_id}/${projectId}`;
    const { data: files, error: listError } = await supabase.storage.from("projects").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list project files: ${listError.message}`);
    }

    // Find the file that starts with the fileId
    const fileToDelete = files?.find((file) => file.name.startsWith(`${fileId}-`));

    if (!fileToDelete) {
      throw new Error("File not found in storage");
    }

    // Delete from storage
    const filePath = `${storagePath}/${fileToDelete.name}`;
    const { error: storageError } = await supabase.storage.from("projects").remove([filePath]);

    if (storageError) {
      throw new Error(`Failed to delete from storage: ${storageError.message}`);
    }

    return true;
  },

  async renameFile(projectId: string, fileId: string, newName: string): Promise<FileMetadata> {
    // Get tenant_id from project record
    const { data: tenantData, error: tenantError } = await supabase
      .from("projects")
      .select("tenant_id")
      .eq("id", projectId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get project tenant: ${tenantError?.message || "Project not found"}`);
    }

    // List files from storage to find the one to rename
    const storagePath = `${tenantData.tenant_id}/${projectId}`;
    const { data: files, error: listError } = await supabase.storage.from("projects").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list project files: ${listError.message}`);
    }

    // Find the file that starts with the fileId
    const fileToRename = files?.find((file) => file.name.startsWith(`${fileId}-`));

    if (!fileToRename) {
      throw new Error("File not found in storage");
    }

    // Build old and new paths
    const oldPath = `${storagePath}/${fileToRename.name}`;
    const newPath = `${storagePath}/${fileId}-${newName}`;

    // Move file to rename it
    const { error: moveError } = await supabase.storage.from("projects").move(oldPath, newPath);

    if (moveError) {
      throw new Error(`Failed to rename file: ${moveError.message}`);
    }

    // Return updated metadata
    return {
      id: fileId,
      name: newName,
      size: fileToRename.metadata?.size || 0,
      type: fileToRename.metadata?.mimetype || "application/octet-stream",
      url: newPath,
      bucket: "projects",
      uploadedAt: fileToRename.created_at || new Date().toISOString(),
      uploadedBy: "unknown",
    };
  },

  // Link table management methods
  async syncProjectAbilities(
    projectId: string,
    clientId: string,
    tenantId: string,
    abilities: { ability: { id: string } }[],
  ): Promise<void> {
    // Delete all existing project abilities
    const { error: deleteError } = await supabase
      .schema("link")
      .from("project_abilities")
      .delete()
      .eq("id", projectId)
      .eq("client_id", clientId)
      .eq("tenant_id", tenantId);

    if (deleteError) {
      throw new Error(`Failed to delete existing project abilities: ${deleteError.message}`);
    }

    // Insert new abilities
    if (abilities.length > 0) {
      const records = abilities.map((a) => ({
        id: projectId,
        client_id: clientId,
        tenant_id: tenantId,
        ability_id: a.ability.id,
      }));

      const { error: insertError } = await supabase.schema("link").from("project_abilities").insert(records);

      if (insertError) {
        throw new Error(`Failed to insert project abilities: ${insertError.message}`);
      }
    }
  },

  async syncProjectRestrictions(
    projectId: string,
    clientId: string,
    tenantId: string,
    restrictions: { restriction: { id: string } }[],
  ): Promise<void> {
    // Delete all existing project restrictions
    const { error: deleteError } = await supabase
      .schema("link")
      .from("project_restrictions")
      .delete()
      .eq("id", projectId)
      .eq("client_id", clientId)
      .eq("tenant_id", tenantId);

    if (deleteError) {
      throw new Error(`Failed to delete existing project restrictions: ${deleteError.message}`);
    }

    // Insert new restrictions
    if (restrictions.length > 0) {
      const records = restrictions.map((r) => ({
        id: projectId,
        client_id: clientId,
        tenant_id: tenantId,
        restriction_id: r.restriction.id,
      }));

      const { error: insertError } = await supabase.schema("link").from("project_restrictions").insert(records);

      if (insertError) {
        throw new Error(`Failed to insert project restrictions: ${insertError.message}`);
      }
    }
  },
};
